import dayjs, { type Dayjs } from 'dayjs';
import { SpecialTerm } from '../../../../enums/clientEnums';
import { DISPLAY_DATE_FORMAT } from '../../constants';
import type { ClientFormAddOn, SubscriptionTerm, YearTerm } from '../../../../types/ClientManagementTypes';

export const YEAR_TERMS: readonly YearTerm[] = ['1', '2', '3', '4', '5'];

const DEMO_WEEKS = 2;

/** The expiry a term produces, counted from `from` (today by default); CURRENT keeps the client's own. */
export const termExpiry = (term: SubscriptionTerm, currentExpiry?: string | null, from: Dayjs = dayjs()) => {
  if (term === SpecialTerm.CURRENT) return currentExpiry ? dayjs(currentExpiry).startOf('day') : null;
  if (term === SpecialTerm.DEMO) return from.add(DEMO_WEEKS, 'week').startOf('day');
  return from.add(Number(term), 'year').startOf('day');
};

/** An add-on's own start, recovered from its saved expiry and length; null when either is missing. */
export const addOnStart = (expiresAt: string | null, years: number | null): Dayjs | null =>
  expiresAt && years !== null ? dayjs(expiresAt).subtract(years, 'year') : null;

/** Reads as "2 years - Expires December 3, 2026" for a length the add-on already holds. */
export const heldLengthLabel = (years: number | null, expiresAt: string | null) => {
  const date = expiresAt ? `Expires ${dayjs(expiresAt).format(DISPLAY_DATE_FORMAT)}` : '';
  if (years === null) return date;
  return `${years === 1 ? '1 year' : `${years} years`}${date ? ` - ${date}` : ''}`;
};

/** The option that ends an add-on with the subscription; its date is the subscription's pending expiry. */
export const untilSubscriptionEndLabel = (limit: SubscriptionTerm | '', currentExpiry?: string | null) => {
  const expiry = limit ? termExpiry(limit, currentExpiry) : null;
  return expiry ? `Until subscription ends - Expires ${expiry.format(DISPLAY_DATE_FORMAT)}` : 'Until subscription ends';
};

const termLength = (term: SubscriptionTerm) => {
  if (term === SpecialTerm.CURRENT) return 'Current';
  if (term === SpecialTerm.DEMO) return `${DEMO_WEEKS} weeks`;
  return term === '1' ? '1 year' : `${term} years`;
};

/** Reads as "2 years - Expires December 3, 2026"; the date is display only. */
export const termLabel = (term: SubscriptionTerm, currentExpiry?: string | null, from?: Dayjs) => {
  const expiry = termExpiry(term, currentExpiry, from);
  const length = termLength(term);
  return expiry ? `${length} - Expires ${expiry.format(DISPLAY_DATE_FORMAT)}` : length;
};

/** What an expiring add-on's length reads as in the summary: the pick, else what the saved row holds; '' when neither. */
export const addOnLengthLabel = (row: Partial<ClientFormAddOn>, limit: SubscriptionTerm | '', currentExpiry?: string | null) => {
  if (row.term === SpecialTerm.DEMO) return termLabel(row.term);
  if (row.term === SpecialTerm.CURRENT) return untilSubscriptionEndLabel(limit, currentExpiry);
  if (row.term) {
    const from = !row.isExpired && (row.heldQuantity ?? 0) > 0 ? addOnStart(row.expiresAt ?? null, row.heldYears ?? null) : null;
    return termLabel(row.term, undefined, from ?? undefined);
  }
  if ((row.heldQuantity ?? 0) === 0) return '';
  return row.isExpired ? 'Expired' : heldLengthLabel(row.heldYears ?? null, row.expiresAt ?? null);
};

/** The integer the payload carries; null for terms the backend derives itself. */
export const termYears = (term: SubscriptionTerm): number | null => (term === SpecialTerm.CURRENT || term === SpecialTerm.DEMO ? null : Number(term));

// CURRENT keeps the client's existing length rather than re-deriving one from today.
export const currentTermYears = (startsAt?: string | null, expiresAt?: string | null): number | null =>
  startsAt && expiresAt ? dayjs(expiresAt).diff(dayjs(startsAt), 'year') : null;

/** True once a term, counted from `from`, would outlast the subscription paying for it. */
export const exceedsTerm = (term: SubscriptionTerm, limit: SubscriptionTerm | '', currentExpiry?: string | null, from?: Dayjs) => {
  if (!limit) return false;
  const limitExpiry = termExpiry(limit, currentExpiry);
  const expiry = termExpiry(term, currentExpiry, from);
  return !!limitExpiry && !!expiry && expiry.isAfter(limitExpiry);
};

// Compared as dates rather than year counts, so an edit's CURRENT limit bounds
// the rows by the client's real expiry.
export const clampTerm = (term: SubscriptionTerm | null, limit: SubscriptionTerm | '', currentExpiry?: string | null): SubscriptionTerm | null => {
  if (!term) return term;
  if (limit === SpecialTerm.DEMO) return SpecialTerm.DEMO;
  // A row left on the demo's two weeks is meaningless once the tier changes.
  if (term === SpecialTerm.DEMO) return null;
  if (!limit) return term;
  return exceedsTerm(term, limit, currentExpiry) ? limit : term;
};
